import {
  filter,
  get,
  has,
  isArray,
  isEmpty,
  isEqual,
  map,
  merge,
  omit,
  reduce,
  set,
  size,
  startCase,
} from 'lodash';
import { ColDef } from 'ag-grid-community';
import { FilterModel } from '@shared/types';
import { FILTER_OPERATORS, FilterModelGroup } from './data-source.interface';
import { prepareOdataString } from '@shared/utils';
import { composeColumnFilter } from '@containers/base/list/xux-list.column-routines';
import { FilterOptionType } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/filter.type';

export const mergeCustomizer: Function = (objValue, srcValue, key): any[] => {
  if (key === 'headerNames') {
    if (isArray(srcValue)) {
      return srcValue.map((columnDefinition: ColDef) =>
        merge(
          {
            headerName: startCase(columnDefinition.field),
            filter: false,
            unSortIcon: false,
          },
          columnDefinition,
        ),
      );
    }

    return srcValue;
  }
};

const createFilterQuery = (value, propertyName): string => {
  const propertyNameReplaced: string = propertyName.replace('.', '/');
  // Replacing one single quote with two ones to fit the OData rules
  const getFilter = (): string => prepareOdataString(get(value, 'filter', ''));
  /* eslint-disable-next-line */
  switch (get(value, 'type') as FilterOptionType) {
    case 'notEqual':
      return `${propertyNameReplaced} eq nin(${getFilter()})`;
    case 'contains':
      return `contains(${propertyNameReplaced},'${getFilter()}')`;
    case 'containsAll':
    case 'containsAny':
    case 'endsWith':
    case 'equals':
    case 'equalsPrice':
    case 'greaterThan':
    case 'greaterThanOrEqual':
    case 'inRange':
    case 'inRangeDate':
    case 'inRangePrice':
    case 'isEqual':
    case 'lessThan':
    case 'lessThanOrEqual':
    case 'mappings':
    case 'notContains':
    case 'startsWith':
    case 'directlyEqual':
      return composeColumnFilter([propertyName, value]);
    default:
      return `contains(${propertyNameReplaced},'${getFilter()}')`;
  }
};

export const composeFilterGroups = (
  filterModel: FilterModel,
  isCombinedFilterQueryWithAnyOf: boolean = false,
): FilterModelGroup => {
  if (isCombinedFilterQueryWithAnyOf && !isEmpty(filterModel)) {
    return {
      [FILTER_OPERATORS.OR]: filterModel,
    };
  }

  return reduce(
    filterModel,
    (accumulator, filterModelColumn, columnName) => {
      const foundedColumns = filter(filterModel, (item) =>
        isEqual(item, filterModelColumn),
      );
      const isMultipleColumns = size(foundedColumns) > 1;
      const filterKeyPath = isMultipleColumns
        ? [FILTER_OPERATORS.OR, columnName]
        : [columnName];

      return set(accumulator, filterKeyPath, filterModelColumn);
    },
    {},
  );
};

export const parseFilterGroups = (
  filterGroups: FilterModelGroup,
  separator: FILTER_OPERATORS = FILTER_OPERATORS.AND,
): string => {
  if (has(filterGroups, FILTER_OPERATORS.OR)) {
    const filterPrams: FilterModelGroup = get(
      filterGroups,
      FILTER_OPERATORS.OR,
    );
    const restFields: FilterModelGroup = omit(
      filterGroups,
      FILTER_OPERATORS.OR,
    );
    const restFilterParams = parseFilterGroups(restFields);
    if (isEmpty(restFilterParams)) {
      return parseFilterGroups(filterPrams, FILTER_OPERATORS.OR);
    }

    return `(${parseFilterGroups(filterPrams, FILTER_OPERATORS.OR)}) ${separator} (${restFilterParams})`;
  }

  const filterPropsArr: string[] = map(filterGroups, createFilterQuery).filter(
    (item) => !isEmpty(item),
  );

  return filterPropsArr.join(` ${separator} `);
};
